import { ref, onMounted, onUnmounted } from 'vue'
import { Client, type IMessage } from '@stomp/stompjs'
import type { FileChangeMessage } from '@/api/contentApi'

const WS_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'

export function useWebSocket() {
  const isConnected = ref(false)
  const error = ref<string | null>(null)
  const client = ref<Client | null>(null)

  // Callback registry for file change notifications
  const fileChangeCallbacks = new Set<(message: FileChangeMessage) => void>()

  /**
   * Connect to WebSocket server
   */
  function connect() {
    // Get WebSocket URL (convert http to ws)
    const wsUrl = WS_URL.replace(/^http/, 'ws')

    // Create STOMP client with native WebSocket
    client.value = new Client({
      brokerURL: `${wsUrl}/ws`,
      debug: (str) => {
        if (import.meta.env.DEV) {
          console.log('[WebSocket]', str)
        }
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000
    })

    // Connection successful
    client.value.onConnect = () => {
      console.log('[WebSocket] Connected')
      isConnected.value = true
      error.value = null

      // Subscribe to file change topic
      client.value?.subscribe('/topic/file-changes', (message: IMessage) => {
        try {
          const fileChange: FileChangeMessage = JSON.parse(message.body)
          console.log('[WebSocket] File change:', fileChange)

          // Notify all registered callbacks
          fileChangeCallbacks.forEach(callback => callback(fileChange))
        } catch (err) {
          console.error('[WebSocket] Error parsing message:', err)
        }
      })
    }

    // Connection error
    client.value.onStompError = (frame) => {
      console.error('[WebSocket] Error:', frame)
      error.value = 'WebSocket connection error'
      isConnected.value = false
    }

    // Connection closed
    client.value.onWebSocketClose = () => {
      console.log('[WebSocket] Connection closed')
      isConnected.value = false
    }

    // Activate the client
    client.value.activate()
  }

  /**
   * Disconnect from WebSocket server
   */
  function disconnect() {
    if (client.value) {
      client.value.deactivate()
      client.value = null
      isConnected.value = false
    }
  }

  /**
   * Register a callback for file change notifications
   */
  function onFileChange(callback: (message: FileChangeMessage) => void) {
    fileChangeCallbacks.add(callback)

    // Return unsubscribe function
    return () => {
      fileChangeCallbacks.delete(callback)
    }
  }

  // Auto-connect on mount
  onMounted(() => {
    connect()
  })

  // Auto-disconnect on unmount
  onUnmounted(() => {
    disconnect()
  })

  return {
    isConnected,
    error,
    connect,
    disconnect,
    onFileChange
  }
}
